import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import Preloader from './Preloader';
import { useTheme } from '../context/ThemeContext';
import PWAInstallPrompt from './PWAInstallPrompt';
import Header from './Header';
import Footer from './Footer';
import LiveChat from './LiveChat';
import ExitIntentPopup from './ExitIntentPopup';
import StickyCTA from './StickyCTA';
import TopBar, { TOPBAR_HEIGHT } from './TopBar';

type LayoutProps = {
  children: React.ReactNode;
};

/**
 * Main layout component that wraps all pages.
 * Provides consistent header, footer, and theme context.
 * 
 * Refactored to use extracted Header and Footer components
 * for better maintainability and reduced complexity.
 */
export default function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [topBarVisible, setTopBarVisible] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Track scroll position for header styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track TopBar visibility from sessionStorage
  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem('dwb_topbar_dismissed');
      setTopBarVisible(dismissed !== 'true');
    } catch {
      // sessionStorage may be unavailable
    }
  }, []);

  // Memoized toggle handler
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  // Memoized close handler
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Handle TopBar dismiss
  const handleTopBarDismiss = useCallback(() => {
    setTopBarVisible(false);
  }, []);

  return (
    <div className={cn('flex flex-col min-h-screen', isDark ? 'dark' : '')}>
      <Preloader />
      <PWAInstallPrompt />

      {/* Top announcement bar */}
      <TopBar onDismiss={handleTopBarDismiss} />

      <Header
        isDark={isDark}
        scrolled={scrolled}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={toggleMobileMenu}
        onCloseMobileMenu={closeMobileMenu}
        topBarVisible={topBarVisible}
      />

      <main 
        className="flex-grow dark:bg-gray-900 dark:text-white"
        style={{ paddingTop: topBarVisible ? TOPBAR_HEIGHT + 64 : 64 }}
      >
        {children}
      </main>

      <StickyCTA />
      <Footer />
      <LiveChat />
      <ExitIntentPopup />
    </div>
  );
}
